import type { VendorWorkType } from '../types';
import {
  RFQ_EMAIL_SIGNATURE_NAME,
  RFQ_PUBLISH_LOGO_URL,
  rfqProjectEmailDescriptor,
  rfqProjectEmailLabel,
} from './rfqPublishEmailPreview';

const PORTAL_URL = 'https://clients.lctimberframes.com/vendor-portal';
const DEFAULT_COMPANY = 'LC Timber Frames';

export type RfqQuoteApprovalEmailContext = {
  recipientFirstName?: string;
  vendorOrgName?: string;
  senderName?: string;
  dealTitle: string;
  projectNumber?: string;
  projectName?: string;
  workType: VendorWorkType;
  customMessage?: string;
  /** Human-readable approved quote line, e.g. "Douglas Fir — $85,000" */
  quoteLabel: string;
  companyName?: string;
  portalUrl?: string;
  logoUrl?: string;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function messageHtml(customMessage: string | undefined, fallbackHtml: string): string {
  const trimmed = (customMessage || '').trim();
  if (!trimmed) return fallbackHtml;
  const inner = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
  return `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">${inner}</p>`;
}

function buildSenderSignature(ctx: RfqQuoteApprovalEmailContext): string {
  const sender = (ctx.senderName || '').trim();
  const organization = escapeHtml(RFQ_EMAIL_SIGNATURE_NAME);
  if (!sender) {
    return `
    <p style="color: #4F423A; margin: 28px 0 0; font-size: 14px; line-height: 1.6;">
      Best regards,<br/>
      <strong>${organization}</strong>
    </p>
  `.trim();
  }
  return `
    <p style="color: #4F423A; margin: 28px 0 0; font-size: 14px; line-height: 1.6;">
      Best regards,<br/>
      <strong>${escapeHtml(sender)}</strong><br/>
      <span style="color: #6B6960;">${organization}</span>
    </p>
  `.trim();
}

export function defaultRfqQuoteApprovalEmailMessage(ctx: {
  recipientFirstName?: string;
  quoteLabel: string;
  workType: VendorWorkType;
}): string {
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  const quote = ctx.quoteLabel.trim() || 'your quote';
  if (ctx.workType === 'cnc_quote') {
    return (
      `Hi ${first},\n\n` +
      `We're pleased to let you know that your CNC quote (${quote}) has been approved.\n\n` +
      `Thank you for your bid — our team will follow up with next steps. Project details remain available in the vendor portal.`
    );
  }
  if (ctx.workType === 'other_quote') {
    return (
      `Hi ${first},\n\n` +
      `We're pleased to let you know that your quote (${quote}) has been approved.\n\n` +
      `Thank you for your bid — our team will follow up with next steps.`
    );
  }
  return (
    `Hi ${first},\n\n` +
    `We're pleased to let you know that your timber quote (${quote}) has been approved.\n\n` +
    `Thank you for your bid — our team will follow up with next steps. Project details remain available in the vendor portal.`
  );
}

export function personalizeRfqQuoteApprovalEmailMessage(
  customMessage: string,
  ctx: { recipientFirstName?: string; quoteLabel: string; workType: VendorWorkType },
): string {
  const trimmed = customMessage.trim();
  if (!trimmed) return trimmed;
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  let msg = trimmed.replace(/^Hi\s+[^,\n]+,/i, `Hi ${first},`);
  const quote = ctx.quoteLabel.trim() || 'your quote';
  if (ctx.workType === 'cnc_quote') {
    msg = msg.replace(
      /your CNC quote \([^)]+\) has been approved/i,
      `your CNC quote (${quote}) has been approved`,
    );
  } else if (ctx.workType === 'other_quote') {
    msg = msg.replace(
      /your quote \([^)]+\) has been approved/i,
      `your quote (${quote}) has been approved`,
    );
  } else {
    msg = msg.replace(
      /your timber quote \([^)]+\) has been approved/i,
      `your timber quote (${quote}) has been approved`,
    );
  }
  return msg;
}

export function rfqQuoteApprovalEmailSubject(
  projectNumber: string | undefined,
  projectDescriptor: string | undefined,
  workType: VendorWorkType,
): string {
  const title = rfqProjectEmailLabel(projectNumber, projectDescriptor);
  if (workType === 'cnc_quote') return `CNC quote approved: ${title}`;
  if (workType === 'other_quote') return `Quote approved: ${title}`;
  return `Timber quote approved: ${title}`;
}

function buildProjectDetailsBlock(ctx: RfqQuoteApprovalEmailContext): string {
  const projectLabel = escapeHtml(
    rfqProjectEmailLabel(
      ctx.projectNumber,
      rfqProjectEmailDescriptor({ descriptor: ctx.projectName, dealTitle: ctx.dealTitle }),
    ),
  );
  const quoteBlock = ctx.quoteLabel.trim()
    ? `<p style="margin: 12px 0 0; font-size: 14px; color: #4F423A; line-height: 1.5;">
        <strong>Approved quote:</strong> ${escapeHtml(ctx.quoteLabel.trim())}
      </p>`
    : '';

  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 28px 0 20px; border-collapse: separate; border-spacing: 0;">
      <tr>
        <td style="background: #F7F5F0; border: 1px solid #E2E0DB; border-radius: 8px; padding: 16px 20px;">
          <p style="margin: 0 0 8px; font-size: 13px; color: #6B6960; text-transform: uppercase; letter-spacing: 0.04em;">Project</p>
          <p style="margin: 0; font-size: 16px; font-weight: 650; color: #2C3E2F;">${projectLabel}</p>
          ${quoteBlock}
        </td>
      </tr>
    </table>
  `.trim();
}

export function buildRfqQuoteApprovalEmailHtml(ctx: RfqQuoteApprovalEmailContext): string {
  const companyNameRaw = ctx.companyName?.trim() || DEFAULT_COMPANY;
  const companyName = escapeHtml(companyNameRaw);
  const portalUrl = ctx.portalUrl?.trim() || PORTAL_URL;
  const logoUrl = ctx.logoUrl?.trim() || RFQ_PUBLISH_LOGO_URL;
  const vendorHeader = escapeHtml((ctx.vendorOrgName || '').trim() || 'Vendor Portal');
  const first = escapeHtml((ctx.recipientFirstName || '').trim() || 'there');
  const quoteEscaped = escapeHtml(ctx.quoteLabel.trim() || 'your quote');

  const fallbackBody =
    ctx.workType === 'cnc_quote'
      ? `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
          Hi ${first},<br/><br/>
          We're pleased to let you know that your CNC quote (${quoteEscaped}) has been approved.<br/><br/>
          Thank you for your bid — our team will follow up with next steps. Project details remain available in the vendor portal.
        </p>`
      : ctx.workType === 'other_quote'
        ? `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
          Hi ${first},<br/><br/>
          We're pleased to let you know that your quote (${quoteEscaped}) has been approved.<br/><br/>
          Thank you for your bid — our team will follow up with next steps.
        </p>`
      : `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
          Hi ${first},<br/><br/>
          We're pleased to let you know that your timber quote (${quoteEscaped}) has been approved.<br/><br/>
          Thank you for your bid — our team will follow up with next steps. Project details remain available in the vendor portal.
        </p>`;

  const body = messageHtml(
    personalizeRfqQuoteApprovalEmailMessage(ctx.customMessage || '', ctx),
    fallbackBody,
  );
  const heading =
    ctx.workType === 'cnc_quote'
      ? 'CNC quote approved'
      : ctx.workType === 'other_quote'
        ? 'Quote approved'
        : 'Timber quote approved';

  const bodyRows = `
          <tr>
            <td style="background: #2C3E2F; padding: 28px 24px 22px; border-radius: 10px 10px 0 0; text-align: center;">
              <img src="${escapeHtml(logoUrl)}" alt="${companyName}" width="160" style="display:block;margin:0 auto 10px;border:0;width:160px;height:auto;max-width:160px;" />
              <p style="color: #C8D4CA; margin: 0 0 4px; font-size: 12px; font-style: italic; letter-spacing: 0.01em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Partnering with</p>
              <p style="color: #FFFFFF; margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.02em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${vendorHeader}</p>
            </td>
          </tr>
          <tr>
            <td style="background: #FFFFFF; padding: 28px 24px; border: 1px solid #E2E0DB; border-top: none; border-radius: 0 0 10px 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <p style="color: #1A1A1A; font-size: 17px; font-weight: 650; margin: 0 0 16px;">${heading}</p>
              ${body}
              ${buildProjectDetailsBlock(ctx)}
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin: 0 0 4px;">
                <tr>
                  <td style="border-radius: 6px; background: #2C3E2F;">
                    <a href="${portalUrl}" style="display: inline-block; padding: 14px 32px; background: #2C3E2F; color: #FFFFFF; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Open Vendor Portal</a>
                  </td>
                </tr>
              </table>
              ${buildSenderSignature(ctx)}
            </td>
          </tr>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(heading)}</title>
</head>
<body style="margin:0;padding:0;background-color:#F7F5F0;-webkit-text-size-adjust:100%;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F7F5F0;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;border-collapse:collapse;">
          ${bodyRows}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();
}
